<template>
	<div class="content">
		<h1>Balance refresh</h1>

		<table v-if="stats" class="margin-top">
			<thead>
				<tr>
					<th>Waiting in fast lane</th>
					<th>Backlog due</th>
					<th>Oldest check</th>
					<th>Refreshed last hour</th>
					<th>Failed last hour</th>
					<th>Held back</th>
					<th>Given up</th>
					<th>Batch (fast / backlog)</th>
					<th>Settled recheck</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>{{ stats.fast_lane_waiting }}</td>
					<td>{{ stats.backlog_due }}</td>
					<td>{{ stats.oldest_check || '-' }}</td>
					<td>{{ stats.refreshed_last_hour }}</td>
					<td :class="{ red: stats.failed_last_hour > 0 }">{{ stats.failed_last_hour }}</td>
					<td>{{ stats.held_back }}</td>
					<td>{{ stats.given_up }}</td>
					<td>{{ stats.settings.priority_batch }} / {{ stats.settings.backlog_batch }}</td>
					<td>{{ stats.settings.settled_refresh_days }} days</td>
				</tr>
			</tbody>
		</table>
		<p v-if="stats" class="small">Numbers as of {{ stats.generated_at }}, refreshed every two minutes.</p>

		<h2 class="margin-top">Failures</h2>

		<p class="margin-top border-black padding">
			Orders the balance refresh job could not recompute. Each is retried
			later with a growing wait (1 hour, 4 hours, 1 day, then weekly).
			Fix the order, then use <em class="bold">Refresh now</em>; a successful refresh
			clears the hold. <em>Give up</em> stops the retries for an order
			that cannot be fixed; <em>Resume</em> puts it back.
		</p>

		<Loader v-if="soft_loading" :soft="true" />

		<p v-if="!records.length" class="margin-top">No orders are held back.</p>

		<Table v-else class="margin-top" :columns="columns" :rows="records" />
	</div>
</template>

<script>
import Vue from 'vue'

export default {
  data: () => ({
    soft_loading: false,
    stats: null,
    records: [],
    columns: {
      order: {
        name: 'Order'
      },
      status: {
        name: 'Status'
      },
      failed_attempts: {
        name: 'Failures'
      },
      last_failed_at: {
        name: 'Last failed'
      },
      next_attempt_at: {
        name: 'Next attempt'
      },
      last_error: {
        name: 'Last error'
      }
    }
  }),
  methods: {
    load () {
      Vue.$api.get('/order-balances/queue-stats', {}, {
        callback: data => this.stats = data
      })
      Vue.$api.get('/order-balances/failures', {}, {
        before: () => this.soft_loading = true,
        always: () => this.soft_loading = false,
        callback: data => this.parse(data)
      })
    },
    refresh (orderId) {
      this.act(orderId, 'refresh')
    },
    giveUp (orderId) {
      this.act(orderId, 'give-up')
    },
    resume (orderId) {
      this.act(orderId, 'resume')
    },
    act (orderId, action) {
      Vue.$api.post('/order/' + orderId + '/balance/' + action, {}, {
        before: () => this.soft_loading = true,
        always: () => this.soft_loading = false,
        callback: () => this.load()
      })
    },
    statusOf (row) {
      if (row.refresh_ignored_at) {
        return 'Given up ' + row.refresh_ignored_at
      }
      if (row.next_attempt_at && new Date(row.next_attempt_at) > new Date()) {
        return 'Held until ' + row.next_attempt_at
      }
      return 'Due for retry'
    },
    parse (data) {
      this.records = data.map(row => {
        if (row.last_error && row.last_error.length > 160) {
          row.last_error = row.last_error.substring(0, 160) + '…'
        }

        row.order = {
          type: 'order-link',
          order_id: row.id
        }

        row.status = this.statusOf(row)

        row.menu = [
          {
            name: 'Refresh now',
            onClick: row => {
              this.refresh(row.id)
            }
          },
          row.refresh_ignored_at
            ? {
              name: 'Resume',
              onClick: row => {
                this.resume(row.id)
              }
            }
            : {
              name: 'Give up',
              onClick: row => {
                this.giveUp(row.id)
              }
            }
        ]

        return row
      })
    }
  },
  created () {
    this.load()

    Vue.$ui.setTitle('Balance refresh')
  }
}
</script>
